import { useMemo } from "react";
import { Butterfly } from "./Icons";
import { between, createRng, pick, round } from "./shapes";

/*
 * The theme's moving air: falling petals, twinkling gold sparkles and butterflies.
 * Everything is placed from a seed, so it is the same on every visit; the motion
 * lives in CSS and stops with prefers-reduced-motion.
 */

const PETAL_COLORS = ["#f79a17", "#ffcd2e", "#e2457f", "#f8b4cc", "#ec7a0c"];

/**
 * Petals drifting down. `spread` scatters their starting heights through a tall page
 * (instead of all starting at its top), so a long page has petals all the way down.
 */
export const Petals = ({ count = 18, seed = 31, spread = false, className = "" }) => {
  const petals = useMemo(() => {
    const rng = createRng(seed);
    return Array.from({ length: count }, (_, i) => ({
      left: round(between(rng, 2, 98)),
      top: spread ? round(between(rng, -4, 70)) : -4,
      size: round(between(rng, 7, 13)),
      duration: round(between(rng, 9, 16)),
      delay: round(between(rng, -16, 0)),
      drift: round(between(rng, -60, 60)),
      color: PETAL_COLORS[i % PETAL_COLORS.length],
    }));
  }, [count, seed, spread]);

  return (
    <div className={`ut-petals ${className}`} aria-hidden="true">
      {petals.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size * 0.7,
            background: p.color,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
};

/** Little four-pointed gold stars that twinkle in turn. */
export const Sparkles = ({ count = 14, seed = 9, className = "" }) => {
  const sparkles = useMemo(() => {
    const rng = createRng(seed);
    return Array.from({ length: count }, () => ({
      left: round(between(rng, 4, 96)),
      top: round(between(rng, 6, 72)),
      size: round(between(rng, 8, 16)),
      duration: round(between(rng, 2.4, 4.2)),
      delay: round(between(rng, -4, 0)),
    }));
  }, [count, seed]);

  return (
    <div className={`ut-sparkles ${className}`} aria-hidden="true">
      {sparkles.map((s, i) => (
        <svg
          key={i}
          viewBox="-10 -10 20 20"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        >
          <path d="M0,-10 C1,-2.4 2.4,-1 10,0 C2.4,1 1,2.4 0,10 C-1,2.4 -2.4,1 -10,0 C-2.4,-1 -1,-2.4 0,-10Z" fill="url(#ut-gold)" />
        </svg>
      ))}
    </div>
  );
};

const WINGS = [
  ["#f06292", "#ffb300"],
  ["#26a69a", "#7e57c2"],
  ["#ffb300", "#e2457f"],
  ["#7e57c2", "#f06292"],
];

/** A few butterflies wandering about the page's side margins. */
export const Butterflies = ({ count = 2, seed = 5, className = "" }) => {
  const list = useMemo(() => {
    const rng = createRng(seed);
    return Array.from({ length: count }, (_, i) => ({
      side: i % 2 ? "right" : "left",
      x: round(between(rng, 3, 14)),
      top: round(between(rng, 12, 70)),
      width: round(between(rng, 18, 26)),
      path: pick(rng, ["a", "b"]),
      duration: round(between(rng, 15, 22)),
      delay: round(between(rng, -20, 0)),
      colors: WINGS[(i + seed) % WINGS.length],
    }));
  }, [count, seed]);

  return (
    <div className={`ut-butterflies ${className}`} aria-hidden="true">
      {list.map((b, i) => (
        <div
          key={i}
          className={`ut-butterflies__one ut-butterflies__one--${b.path}`}
          style={{
            [b.side]: `${b.x}%`,
            top: `${b.top}%`,
            width: b.width,
            animationDuration: `${b.duration}s`,
            animationDelay: `${b.delay}s`,
          }}
        >
          <Butterfly colors={b.colors} />
        </div>
      ))}
    </div>
  );
};
